import React, { Component, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { reportError } from "@/lib/errorTracking";
import { isChunkLoadError, isRenderChunkLoadError, selfHealChunkReload } from "@/lib/chunkReload";

interface Props {
  children: ReactNode;
  fallbackLevel?: "page" | "tab";
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // A failed lazy() chunk import (usually a stale bundle mid-deploy) is thrown
    // through Suspense and caught HERE during render — it never fires the
    // window-level `error`/`unhandledrejection`/`vite:preloadError` listeners in
    // main.tsx, so this is the only place that can self-heal it. Reload once (the
    // shared per-minute guard prevents a loop) to fetch a fresh bundle; if we're
    // rate-limited, fall through to the fallback UI's manual "Reload" button. We
    // do NOT report chunk errors — they're expected, self-healing deploy
    // transients, and reporting them spammed the founder's crash alerts (Sentry
    // "Importing a module script failed", source=ErrorBoundary:page — audit
    // 2026-07-19).
    if (isChunkLoadError(error?.message)) {
      // Known stale-deploy chunk error — self-heal and don't report (expected).
      selfHealChunkReload();
      return;
    }
    if (isRenderChunkLoadError(error?.message)) {
      // WebKit/iOS bare "Load failed" during render — almost always a failed
      // lazy() chunk mid-deploy. Try ONE reload to pull a fresh bundle; only if
      // we're rate-limited (already reloaded this minute → likely NOT a
      // transient chunk error) do we fall through to report it + show the
      // fallback UI, so a genuine recurring failure stays visible in Sentry.
      if (selfHealChunkReload()) return;
    }
    reportError(error, `ErrorBoundary:${this.props.fallbackLevel || "page"}:${info.componentStack?.slice(0, 200)}`);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const isTab = this.props.fallbackLevel === "tab";

      return (
        <div className={`flex flex-col items-center justify-center text-center px-6 ${isTab ? "py-16" : "min-h-screen bg-background"}`}>
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
            <AlertTriangle className="w-7 h-7 text-primary" />
          </div>
          <h2 className="text-lg font-semibold text-foreground mb-2" style={{ fontFamily: "Syne, sans-serif" }}>
            {isTab ? "This section hit a snag" : "Something went wrong"}
          </h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-sm">
            {isTab
              ? "An error occurred loading this tab. Your other tabs still work fine."
              : "An unexpected error occurred. Please try refreshing the page."}
          </p>
          <button
            onClick={isTab ? this.handleRetry : () => window.location.reload()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            {isTab ? "Try again" : "Reload page"}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
